import React from 'react';
import Select from 'react-select';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

const FormComp = ({ formFields, apiPath, initialData }) => {
  const [formData, setFormData] = React.useState([]);
  const [selectOptionsByName, setSelectOptionsByName] = React.useState({});
  const [state, setState] = React.useState('create');
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const [statusType, setStatusType] = React.useState('');
  const fileInputRefs = React.useRef({});
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'rgb(81, 84, 82)',
      borderColor: '#444',
      color: '#fff',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'rgb(81, 84, 82)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(81, 84, 82)',
      color: '#fff',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    input: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#ccc',
    }),
  }

  React.useEffect(() => {
    handleFormFields();
  }, [formFields]);

  React.useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => {
        const next = { ...prev };
        Object.keys(initialData).forEach(key => {
          if (key in next) {
            next[key] = initialData[key];
            let elements = document.getElementsByName(key);
            if (elements.length > 0) {
              elements[0].value = initialData[key];
            }
          }
        });
        return next;
      });
      setState('edit');
    }
  }, [initialData]);

  const [error, setError] = React.useState(false);

  const toLocalDatetime = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const emptyFormData = () => {
    let empty = {};
    Object.values(formFields).forEach(([name, field]) => {
      empty[name] = field.type === 'checkbox' ? false : field.type === 'multiSelect' ? [] : '';
    });
    return empty;
  };

  function handleFormFields() {
    let newFormData = emptyFormData();
    let selectFields = [];
    Object.values(formFields).forEach(([name, field]) => {
      if ((field.type === 'select' || field.type === 'multiSelect') && field.data) {
        selectFields.push({ name, data: field.data });
      }
    });

    if (apiPath === 'schedule') {
      const start = new Date();
      start.setHours(18, 0, 0, 0);
      const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
      newFormData.timeStart = toLocalDatetime(start);
      newFormData.timeEnd = toLocalDatetime(end);
      newFormData.youtubeChannel = 'NaruxForAll';
    }

    if (selectFields.length > 0) {
      selectFields.forEach((field) => {
        getSelectOptions(field.data, field.name);
      });
    } else {
      setSelectOptionsByName({});
    }
    setFormData(newFormData);
    setState('create');
  }

  // Los <input type="file"> son no controlados, así que resetear formData no los vacía:
  // hay que limpiar el DOM a mano o el input sigue mostrando el fichero anterior.
  const clearFileInputs = () => {
    Object.values(fileInputRefs.current).forEach((el) => { if (el) el.value = ''; });
  };

  const handleInputChange = (event) => {
    const { name, value, files, type, checked } = event.target;
    setStatus('');
    if (type === 'file' && files[0] && files[0].size > 2 * 1024 * 1024) {
      setStatus('El archivo de ' + name + ' excede el límite de 2MB');
      setStatusType('error');
      event.target.value = '';
      return;
    }
    if (name === 'timeStart' && value) {
      const [datePart, timePart] = value.split('T');
      // si el navegador entrega solo fecha (hora 00:00), aplicar 18:00 por defecto
      const startValue = timePart === '00:00' ? `${datePart}T18:00` : value;
      setFormData(prev => {
        // Al mover el inicio se conserva la duración (2 h si aún no hay una válida).
        const duration = new Date(prev.timeEnd) - new Date(prev.timeStart);
        const endDate = new Date(new Date(startValue).getTime() + (duration > 0 ? duration : 2 * 60 * 60 * 1000));
        return { ...prev, timeStart: startValue, timeEnd: toLocalDatetime(endDate) };
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file'
        ? files[0]
        : type === 'checkbox'
          ? checked
          : value,
    }));
  };

  const getSelectOptions = (dataType, fieldName) => {
    const requestOptions = {
      method: 'POST',
    };
    const actionByType = {
      rewards: 'getRewardsOption',
      games: 'getGamesOption',
      activeGames: 'getGamesOption&active=1',
      gameNames: 'getGameNamesOption',
      missions: 'getMissionsOption',
      events: 'getEventsOption',
      schedules: 'getSchedulesOption',
      codes: 'getCodesOption',
    };
    const action = actionByType[dataType];
    if (!action) return;
    fetch('/api/adminLogin' + '?action=' + action, requestOptions)
      .then(response => response.json())
      .then(data => {
        setSelectOptionsByName(prev => ({
          ...prev,
          [fieldName]: data
        }));
      });
  }

  const getSelectedOption = (option, dataType) => {
    const actionByType = {
      rewards: 'getRewardOption',
      games: 'getGameOption',
      missions: 'getMissionOption',
      schedules: 'getScheduleOption',
      codes: 'getCodeOption',
    };
    const action = actionByType[dataType];
    if (!action) return;
    const data = new FormData();
    data.append('id', option.value);
    const requestOptions = {
      method: 'POST',
      body: data
    };
    fetch('/api/adminLogin' + '?action=' + action, requestOptions)
      .then(response => response.json())
      .then(data => {
        // Se parte de vacio: un campo sin valor no viene en el documento de Mongo, y si se
        // partiera de lo anterior se quedaria el valor del elemento visitado antes.
        setFormData(() => {
          const next = emptyFormData();
          // Las fechas llegan en UTC; el <input type="datetime-local"> las quiere en hora local.
          formFields.forEach(([name, field]) => {
            if (field.type === 'datetime-local' && data[name]) data[name] = toLocalDatetime(new Date(data[name]));
          });
          Object.keys(data).forEach(key => {
            let elements = document.getElementsByName(key);
            if (elements.length > 0 && key != 'image') {
              if (key === 'description') {
                elements[1].value = data[key];
              } else {
                elements[0].value = data[key];
              }
            }
            next[key] = data[key];
            if (key === '_id') next.selectToEdit = data[key];
          });
          return next;
        });
        setState('edit');
      });
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!Array.isArray(formData)) {
      // Campos que el formulario marca con optional: true pueden ir vacios.
      const fields = Object.fromEntries(formFields);
      for (let key in formData) {
        if ((!(key in formData) || formData[key] === '') && !(fields[key] && fields[key].optional) &&
          key != 'instagramVideo' && key != 'youtubeVideo' && key != 'thumbnail' && key != 'discordChannelId' &&
          (e.target.dataset.action !== 'edit' && (key !== 'image' && key !== 'selectToEdit'))) {
          setError(key);
          return;
        }
      }
      setError(false);

      const data = new FormData();
      for (const key in formData) {
        data.append(key, formData[key]);
      }

      const requestOptions = {
        method: 'POST',
        body: data
      };
      const url = '/api/adminLogin' + '?action=' + e.target.dataset.action + '&type=' + apiPath;

      if (apiPath !== 'schedule') {
        if (e.target.dataset.action === 'delete' && !window.confirm('¿Borrar definitivamente?')) return;
        const res = await fetch(url, requestOptions);
        let payload = null;
        try { payload = await res.json(); } catch (err) { /* respuesta sin cuerpo JSON */ }
        // Un rechazo del servidor (4xx/5xx) no limpia el formulario: se enseña el motivo.
        if (!res.ok) {
          setStatus('Error: ' + ((payload && payload.error) || res.status));
          setStatusType('error');
          return;
        }
        clearFileInputs();
        handleFormFields();
        if (payload && payload.message) {
          setStatus(payload.message);
          setStatusType('success');
        }
        return;
      }

      // El endpoint de schedule dispara Twitch + YouTube + Discord: un doble click crea dos directos.
      if (loading) return;
      const action = e.target.dataset.action;
      if (action === 'delete' && !window.confirm('¿Borrar este directo del calendario y de Twitch, YouTube y Discord?')) return;
      const verb = { create: 'crear', edit: 'editar', delete: 'borrar' }[action];
      setLoading(true);
      setStatus('');
      setStatusType('');
      try {
        const res = await fetch(url, requestOptions);
        let payload = null;
        try { payload = await res.json(); } catch (err) { /* respuesta sin cuerpo JSON */ }
        if (res.ok && payload !== false) {
          clearFileInputs();
          handleFormFields();
          const networks = payload && payload.networks
            ? ' ' + Object.entries(payload.networks).map(([red, how]) => red + ': ' + how).join(' · ')
            : '';
          setStatus(action === 'create'
            ? 'Horario creado con éxito. Formulario limpio para el siguiente.'
            : (action === 'edit' ? 'Directo editado.' : 'Directo borrado.') + networks);
          setStatusType('success');
        } else {
          setStatus(payload && payload.error === 'admin_required'
            ? 'Sesión de admin caducada, vuelve a entrar. No se ha cambiado nada.'
            : 'Error al ' + verb + ' el directo: ' + ((payload && payload.error) || 'revisa los campos y reintenta.'));
          setStatusType('error');
        }
      } catch (err) {
        setStatus('Error de red al ' + verb + ' el directo. Recarga y comprueba si se ha guardado.');
        setStatusType('error');
      } finally {
        setLoading(false);
      }
    }
  }
  return (
    <div className='formOver'>
      {formFields.map(([name, field]) => (
        field.type === 'checkbox' ? (
          <div className={field.className ?? ''} key={name}>
            <FormControlLabel
              control={
                <Checkbox
                  name={name}
                  checked={!!formData[name]}
                  onChange={handleInputChange}
                />
              }
              label={field.placeholder ?? field.label ?? name}
            />
          </div>
        ) : (
          <div className='formFieldRow' key={name}>
            <label className='formFieldLabel heroes'>{field.placeholder}</label>
            {field.type === 'select' ? (
              <Select className='heroes selectForm'
                options={selectOptionsByName[name] ?? []}
                isSearchable
                styles={customStyles}
                // Controlado: al cargar un elemento se ve su valor (p.ej. el juego de la
                // recompensa) y al limpiar el formulario se vacia, tambien el selector de editar.
                value={(selectOptionsByName[name] ?? []).find(opt => opt.value === formData[name]) || null}
                onChange={(selectedOption) => {
                  if (name === 'selectToEdit') {
                    getSelectedOption(selectedOption, field.data);
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      [name]: selectedOption?.value ?? ''
                    }));
                  }
                }}
              />
            ) : field.type === 'multiSelect' ? (
              // Varios valores (p.ej. los eventos de un directo del calendario). Vacio vale:
              // [] no cuenta como campo sin rellenar. Se envia separado por comas.
              <Select className='heroes selectForm'
                options={selectOptionsByName[name] ?? []}
                isMulti
                isSearchable
                styles={customStyles}
                placeholder={field.placeholder}
                value={(selectOptionsByName[name] ?? []).filter(opt => (formData[name] || []).includes(opt.value))}
                onChange={(selected) => {
                  setFormData(prev => ({
                    ...prev,
                    [name]: (selected || []).map(opt => opt.value)
                  }));
                }}
              />
            ) : field.type === 'staticSelect' ? (
              <Select className='heroes selectForm'
                options={field.options}
                isSearchable
                styles={customStyles}
                placeholder={field.placeholder}
                value={field.options.find(opt => opt.value === formData[name]) || null}
                onChange={(selectedOption) => {
                  setFormData(prev => ({
                    ...prev,
                    [name]: selectedOption?.value ?? ''
                  }));
                }}
              />
            ) : (
              <input
                className={field.className ?? ''}
                placeholder={field.placeholder}
                type={field.type}
                name={name}
                ref={field.type === 'file' ? (el) => { fileInputRefs.current[name] = el; } : undefined}
                onChange={handleInputChange}
                accept={field.type === "file" ? "image/*" : undefined}
                checked={field.type === "checkbox" ? formData[name] : undefined}
                value={field.type === 'file' ? undefined : formData[name] ?? ''}
                onWheel={field.type === 'number' ? (e) => e.currentTarget.blur() : undefined}
              />
            )}
          </div>
        )
      ))}
      {formFields.length > 0 && (
        <div className='navTops heroes unselectable'>
          {state === 'create' ? (
            <div id='createBtn' data-action="create"
              onClick={(e) => { if (!loading) { handleSubmitCreate(e); } }}>
              {loading ? 'Creando...' : 'Create'}
            </div>
          ) : null}
          {state === 'edit' ? (
            <>
              <div id='editBtn' data-action="edit" onClick={handleSubmitCreate}>{loading ? 'Guardando...' : 'Edit'}</div>
              <div id='deleteBtn' data-action="delete" onClick={handleSubmitCreate}>Delete</div>
            </>
          ) : null}

        </div>
      )}
      {error && <p>El campo {error} está vacío.</p>}
      {loading ? <p>Cargando...</p> : null}
      {status ? (
        <p
          role={statusType === 'error' ? 'alert' : 'status'}
          style={{
            padding: '8px 12px',
            borderRadius: 4,
            fontWeight: 600,
            color: statusType === 'success' ? '#0a6b0a' : statusType === 'error' ? '#8a1a1a' : 'inherit',
            backgroundColor: statusType === 'success' ? '#d9f5d9' : statusType === 'error' ? '#fdecea' : 'transparent'
          }}
        >
          {status}
        </p>
      ) : null}
    </div>
  )
}

export default FormComp
